import React from 'react';
import { Box, Text, VStack } from '@chakra-ui/react';
import SiteHero from '../Components/SiteLanding/SiteHero';
import SiteTextToOrder from '../Components/SiteLanding/SiteTextToOrder';
import SiteServices from '../Components/SiteLanding/SiteServices';
import SiteHowItWorks from '../Components/SiteLanding/SiteHowItWorks';
import SiteRecurring from '../Components/SiteLanding/SiteRecurring';
import SiteMembership from '../Components/SiteLanding/SiteMembership';
import SitePricing from '../Components/SiteLanding/SitePricing';
import SiteLocation from '../Components/SiteLanding/SiteLocation';
import SiteAbout from '../Components/SiteLanding/SiteAbout';
import SiteFooter from '../Components/SiteLanding/SiteFooter';
import SiteNavbar from '../Components/SiteLanding/SiteNavbar';
import PriceOfferBar from '../Components/SiteLanding/PriceOfferBar';
import { smsHref } from '../Components/SiteLanding/textOrderLinks';
import ChatWidget from '../Components/Chat/ChatWidget';

/**
 * SiteLandingPage — Public-facing website for a laundry.
 * Multi-tenant: receives laundry config as props and renders accordingly.
 * Each laundry gets their own branded landing page at /:laundryId/site
 */
const SiteLandingPage = ({ laundryConfig }) => {
    // Generate anonymous visitor ID for chat (persists in localStorage)
    const getVisitorId = () => {
        let visitorId = localStorage.getItem('chatVisitorId');
        if (!visitorId) {
            visitorId = 'visitor-' + Math.random().toString(36).substr(2, 9);
            localStorage.setItem('chatVisitorId', visitorId);
        }
        return visitorId;
    };

    const laundryId = laundryConfig?.laundryId || '1';
    const visitorId = getVisitorId();

    // Optional per-tenant section hide-flags (site_content). When a flag is
    // absent the section renders exactly as before — no change for existing
    // tenants. Home-based / pickup-delivery-only operators can opt to hide
    // marketing sections that don't fit their business.
    const sc = laundryConfig?.siteContent || {};

    return (
        <Box bg="white" minH="100vh">
            <SiteNavbar config={laundryConfig} />
            <PriceOfferBar config={laundryConfig} />
            {String(laundryId) === '1' && (
                <Box bg="white" color="gray.800" py={4} px={4} textAlign="center" borderBottom="1px solid" borderColor="gray.200">
                    <Text fontSize={{ base: 'md', md: 'lg' }} maxW="3xl" mx="auto" data-testid="home-text-schedule">
                        Wash &amp; Fold Laundry Pickup in Round Rock, TX. Text{' '}
                        <Text as="a" href={smsHref('+15127820428', '')} fontWeight="800" color="teal.700" textDecoration="underline">
                            (512) 782-0428
                        </Text>
                        {' '}to schedule laundry pickup in Round Rock, TX.{' '}
                        <Text as="a" href="/wash-and-fold" color="teal.700" fontWeight="700" textDecoration="underline">
                            Wash and fold details
                        </Text>
                    </Text>
                </Box>
            )}
            {String(laundryId) === '1' && (
                <Box bg="teal.800" color="white" py={{ base: 8, md: 10 }} px={4} textAlign="center">
                    <VStack spacing={2} maxW="3xl" mx="auto" data-testid="home-ozone-wash">
                        <Text fontSize="sm" fontWeight="700" letterSpacing="0.14em" textTransform="uppercase" color="teal.200">
                            Ozone wash
                        </Text>
                        <Text fontSize={{ base: '2xl', md: '4xl' }} fontWeight="800" lineHeight="1.1">
                            Cleaner clothes for people who care about germs
                        </Text>
                        <Text fontSize={{ base: 'md', md: 'lg' }} color="teal.50">
                            Every wash-and-fold load is an ozone wash. Cold water, built to reduce germs, bacteria, and viruses on the fabric.
                        </Text>
                        <Text as="a" href="/ozone-wash" mt={1} fontSize={{ base: 'md', md: 'lg' }}
                            fontWeight="700" color="white" textDecoration="underline">
                            See how ozone wash works
                        </Text>
                    </VStack>
                </Box>
            )}
            {String(laundryId) === '1' && (
                <Box bg="gray.900" color="white" py={{ base: 6, md: 8 }} px={4} textAlign="center">
                    <VStack spacing={1} data-testid="home-comforter-sale">
                        <Text fontSize={{ base: 'xl', md: '3xl' }} fontWeight="800" color="orange.300" lineHeight="1.1">
                            Comforter cleaning sale
                        </Text>
                        <Text fontSize={{ base: '4xl', md: '6xl' }} fontWeight="800" lineHeight="0.95">
                            20% off
                        </Text>
                        <Text fontSize={{ base: 'lg', md: '2xl' }} fontWeight="700">
                            Use code <Text as="span" fontFamily="mono">COMFY</Text>
                        </Text>
                        <Text as="a" href="/1/comforter-cleaning" mt={2} fontSize={{ base: 'md', md: 'lg' }}
                            fontWeight="600" color="teal.200" textDecoration="underline">
                            Learn about comforter cleaning
                        </Text>
                    </VStack>
                </Box>
            )}
            <SiteHero config={laundryConfig} />
            <SiteTextToOrder config={laundryConfig} />
            {laundryConfig?.siteContent?.services?.length > 0 && (
                <SiteServices config={laundryConfig} />
            )}
            {!sc.hideHowItWorks && <SiteHowItWorks config={laundryConfig} />}
            <SiteRecurring config={laundryConfig} />
            <SiteMembership config={laundryConfig} />
            {!sc.hidePricing && <SitePricing config={laundryConfig} />}
            {!sc.hideLocation && <SiteLocation config={laundryConfig} />}
            {!sc.hideAbout && <SiteAbout config={laundryConfig} />}
            <SiteFooter config={laundryConfig} />
            <ChatWidget
                customerId={visitorId}
                laundryId={laundryId}
                customerName="Website Visitor"
                customerPhone=""
            />
        </Box>
    );
};

export default SiteLandingPage;
